import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import PrintableTable from "./Components/PrintableTable";
import PrintHeader from "../../Components/PrintHeader/PrintHeader";
import CreditServices from "../../Services/QRCodeServices/CreditServices/CreditServices";

function CreditList() {
  const [creditItems, setCreditItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (row.id === "total-row" ? "" : index + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "Customer Name",
      selector: (row) => row.customerName,
      sortable: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.customerName}
        </div>
      ),
    },
    {
      name: "Total Amount",
      selector: (row) => row.amount,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.amount.toFixed(2)}
        </div>
      ),
    },
    {
      name: "Paid Amount",
      selector: (row) => row.paidAmount,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.paidAmount.toFixed(2)}
        </div>
      ),
    },
    {
      name: "Remaining Amount",
      selector: (row) => row.remainingAmount,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.remainingAmount.toFixed(2)}
        </div>
      ),
    },
  ];

  async function fetchCreditList() {
    try {
      setLoading(true);
      const fetchedItems = await CreditServices.getList();
      setCreditItems(fetchedItems);
      setFilteredItems(fetchedItems);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching credit list:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCreditList();
  }, []);

  useEffect(() => {
    let filtered = creditItems;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.customerName?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredItems(filtered);
  }, [searchTerm, creditItems]);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  const totalAmount = filteredItems.reduce((sum, row) => sum + row.amount, 0);
  const totalPaidAmount = filteredItems.reduce(
    (sum, row) => sum + row.paidAmount,
    0
  );
  const totalRemainingAmount = filteredItems.reduce(
    (sum, row) => sum + row.remainingAmount,
    0
  );

  const totalRow = {
    customerName: "Total",
    amount: totalAmount,
    paidAmount: totalPaidAmount,
    remainingAmount: totalRemainingAmount,
    id: "total-row",
  };

  const tableData =
    filteredItems.length > 0 ? [...filteredItems, totalRow] : filteredItems;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Credit List Report" pageTitle="Credit List" />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <PrintHeader reportTitle="Credit List Report" />
                  <div className="d-print-none">
                    <div className="d-flex flex-wrap gap-2">
                      <FormGroup className="col-lg-3">
                        <Input
                          type="text"
                          placeholder="Search by Customer Name"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                      <div className="ms-auto">
                        {filteredItems.length > 0 && (
                          <Button
                            color="outline-secondary"
                            className="py-2 px-3 d-flex align-items-center shadow-sm"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line fs-16"></i>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  {isPrinting ? (
                    <PrintableTable creditItems={filteredItems} />
                  ) : (
                    <DataTable
                      columns={columns}
                      data={tableData}
                      customStyles={customStyles}
                      responsive
                      striped
                      highlightOnHover
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      progressPending={loading}
                      progressComponent={
                        <div className="my-3">
                          <Triangle
                            visible={true}
                            height="80"
                            width="80"
                            color="#5B71B9"
                            ariaLabel="triangle-loading"
                          />
                          <h5 className="mt-1">Loading...</h5>
                        </div>
                      }
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default CreditList;
